import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Cal, { getCalApi } from "@calcom/embed-react";
import PageHeader from "../../../components/layout/PageHeader";
import { buttonClasses, PinnedActionBar, useToast } from "../../../components/ui";
import { SUPPORT_SETTINGS_PATH } from "../../../routes/paths";
import { useSignedInUser } from "../../auth/hooks/useSignedInUser";
import { formatMeetingWhen } from "../components/SupportHistory";
import { settingsCrumbs } from "../settingsTabs";

/**
 * The event type the booker opens onto, as `organisation/event`.
 *
 * Hard-coded rather than configured: it is the same calendar for every
 * workspace, and a support line that could point somewhere else per environment
 * is a way of quietly booking real people onto a staging diary.
 */
const CAL_LINK = "naurist/support";

/**
 * Cal keeps one instance per namespace, so this name is what separates the
 * support booker from any other embed added later — they would otherwise share
 * a configuration and overwrite each other's.
 */
const CAL_NAMESPACE = "support";

/**
 * How often a booker nobody has touched goes back for the times again, in ms.
 *
 * Cal works out what is free when the booker loads and then leaves that answer
 * on the screen. Two people picking at once is the ordinary case for one
 * support diary, and the one who opened the month first is still being offered
 * the eleven o'clock the other took at half past ten. Cal refuses the booking
 * at the end — nobody is double-booked by this — but being turned away after
 * picking a time is a worse way to find out than the time quietly not being
 * there, which is what everyone expects from anything that sells seats.
 *
 * A minute, because that is the scale of the race. Shorter is a page that
 * reloads itself while somebody is reading it.
 */
const AVAILABILITY_REFRESH_MS = 60_000;

/**
 * What sends somebody here, in the two ways it happens.
 *
 * The Support tab's form hands over the reason and the message; a Modify in the
 * history hands over the booking being moved. Never both — one is a call that
 * does not exist yet and the other is one that does.
 *
 * Optional throughout, and read defensively: router state is whatever the
 * previous screen put there, and this page is a URL like any other — someone
 * arrives from a bookmark, a reload, or a second tab with nothing attached.
 */
interface BookCallState {
  reason?: string;
  message?: string;
  /** Cal's id for the call being moved, when this was reached from Modify. */
  rescheduleUid?: string;
  /**
   * When that call currently is, so this screen can say so above the booker.
   *
   * Carried alongside the id rather than looked up again: the row that sent
   * them here already had it. Said here because the booker does not say it
   * where it can be read — Cal marks the former time inside the iframe, on a
   * panel that is off the bottom of a phone and gone the moment a new day is
   * picked, and somebody moving a call needs to know what they are moving it
   * from while they choose.
   */
  currentStartsAt?: string;
}

/**
 * Picking a time for a support call: Cal's booker, on a screen of its own.
 *
 * A screen rather than a dialog or a panel under the form. A month of
 * availability is a page's worth of screen however it is framed, and booking a
 * call is a thing somebody goes off to do and comes back from — so it gets a
 * URL, a title, and a trail back to the tab that sent them, instead of being a
 * layer over a form they have already finished with.
 *
 * The embed rather than a link out to cal.com: sending somebody to another tab
 * to finish loses both the context they just typed and, often, the person.
 *
 * What they wrote arrives in router state and goes onto the booking as its
 * notes, alongside the name and address Clerk already holds. None of it is
 * asked for twice, and whoever takes the call opens an invite that already says
 * what it is about. Arriving with no state — a reload, a bookmark — is not a
 * broken page: the booker simply opens unfilled, which is exactly what a
 * calendar reached directly should do.
 *
 * Moving a call lands here too, from Modify in the history, with the booking's
 * id and the time it is currently for instead of a reason. One screen rather
 * than two: rescheduling is picking a time out of the same month, and Cal's
 * booker does both from the same embed — a second page would be this one with a
 * different word on it. The time being moved is written under the heading,
 * because the booker keeps its own note of it somewhere nobody scrolls to.
 *
 * The one thing this page does to the booker rather than around it is make it
 * ask again — a month of availability read once and left up goes on offering
 * times other people have taken since. See the second effect below, which is
 * careful about it for reasons worth reading before touching.
 */
const BookCallPage: React.FC = () => {
  const toast = useToast();
  const { state } = useLocation();
  const { fullName, email } = useSignedInUser();

  const { reason, message, rescheduleUid, currentStartsAt } = (state ??
    {}) as BookCallState;

  // What the booker is replaced with when the times on it have gone stale. A
  // new key is a new element, a new iframe, and a month of availability read
  // fresh — see `AVAILABILITY_REFRESH_MS`, and see below for why this is the
  // whole of the lever: Cal's embed API is `ui`, `inline`, `on` and `off`, and
  // the query that holds the times is on the far side of the frame.
  const [bookerKey, setBookerKey] = useState(0);

  // The pane the booker is drawn into, held so that a click landing inside it
  // can be told apart from a click on the page around it.
  const bookerRef = useRef<HTMLDivElement>(null);

  // Whether they have started picking, which is what makes reloading safe or
  // ruinous. The iframe is everything they have done so far — the slot, the
  // reason, half a phone number — and replacing it throws all of it away. Only
  // a booker nobody has touched is replaced.
  const startedRef = useRef(false);

  // Whether the booker is showing "This meeting is scheduled". State rather
  // than a ref, because the page grows a way onward when it turns true: that
  // screen is where somebody is finished, and the only ways off it were the
  // breadcrumb and the browser's own Back.
  const [scheduled, setScheduled] = useState(false);

  // The reason above what they wrote, so the invite says what the call is about
  // before anybody opens it. Empty when neither survived the trip here, rather
  // than a stray blank line on the booking.
  const notes = [reason, message].filter(Boolean).join("\n\n");

  // Moving a call rather than making one. The same screen either way — it is the
  // same month of availability, and Cal's booker is the thing that differs, not
  // the page around it — but it should not call itself booking when the call is
  // already booked.
  const isReschedule = Boolean(rescheduleUid);
  const heading = isReschedule
    ? "Reschedule your support call"
    : "Book a support call";

  // The time they are moving away from, where the row that sent them here said
  // so. Parsed before it is written out: this is router state, which is to say
  // whatever the previous screen put there, and a date that will not read is a
  // line of `NaN` under the heading rather than a missing one.
  const currentWhen =
    isReschedule &&
    currentStartsAt &&
    Number.isFinite(Date.parse(currentStartsAt))
      ? formatMeetingWhen(currentStartsAt)
      : null;

  useEffect(() => {
    let cancelled = false;
    let api: Awaited<ReturnType<typeof getCalApi>> | null = null;

    // Declared out here so the same reference is handed to `on` and to `off`;
    // an inline arrow would leave the listener attached forever.
    const handleBooked = () => {
      // Nothing is replaced under a confirmation. Clicking into the booker has
      // already said as much, and this is the belt to that pair of braces: the
      // screen with the joining link on it is the one screen worth keeping.
      startedRef.current = true;
      setScheduled(true);
      // Cal draws its own confirmation, with the time and the joining link on
      // it, and the page stays on that — navigating away the moment a booking
      // lands would take the one screen that says what was booked. This is the
      // app agreeing with it, for anyone who has already scrolled past.
      toast.success(
        "Your call is booked",
        "The invite is on its way to your email."
      );
    };

    // Its own event and its own sentence: Cal fires this instead of the one
    // above when a call is moved, and "booked" is the wrong word for a time
    // somebody has already been sent an invite to once.
    const handleRescheduled = () => {
      startedRef.current = true;
      setScheduled(true);
      toast.success(
        "Your call has been moved",
        "The updated invite is on its way to your email."
      );
    };

    (async () => {
      const cal = await getCalApi({ namespace: CAL_NAMESPACE });
      // Navigating away while the script is still loading leaves nothing to
      // configure, and configuring an unmounted booker throws.
      if (cancelled) return;
      api = cal;

      cal("ui", {
        theme: "light",
        cssVarsPerTheme: {
          // Our own brand teal, not Cal's default: the booker sits inside the
          // app, and a second accent colour reads as a different product.
          light: { "cal-brand": "#00C2CB" },
          dark: { "cal-brand": "#00C2CB" },
        },
        hideEventTypeDetails: false,
        layout: "month_view",
      });

      // Both, unconditionally: which of the two fires is Cal's business, and a
      // page that only listened for the one it expected would go quiet the day
      // somebody rebooked from inside the booker.
      cal("on", { action: "bookingSuccessful", callback: handleBooked });
      cal("on", {
        action: "rescheduleBookingSuccessful",
        callback: handleRescheduled,
      });
    })();

    return () => {
      cancelled = true;
      api?.("off", { action: "bookingSuccessful", callback: handleBooked });
      api?.("off", {
        action: "rescheduleBookingSuccessful",
        callback: handleRescheduled,
      });
    };
  }, [toast]);

  /**
   * Keeping the times honest while the page sits there.
   *
   * A slot somebody else takes should stop being offered here, the way a seat
   * somebody else takes stops being offered anywhere that sells seats. Cal does
   * hold a slot the moment another booker reaches the form and does refuse a
   * booking on a time that has gone — the part that is missing is this page
   * ever asking again. So it asks: the booker is replaced, and the new one
   * loads a month that has the other person's booking in it.
   *
   * Replaced rather than refreshed because there is no refreshing it. Cal's
   * embed takes `ui`, `inline`, `on` and `off` and nothing that says fetch the
   * times again, and what holds them is inside a cross-origin iframe. A new
   * element is the only way back to the server.
   *
   * Which is why nothing is replaced once they have clicked into it. A reload
   * mid-booking would take the slot they picked and everything they typed, and
   * that is a far worse thing to do to somebody than showing them a time that
   * has since gone. Clicking into a cross-origin frame is the one thing this
   * side of it can still see — the page loses focus while the frame becomes
   * the active element — and after that this stops for good. The failure this
   * is built to have is doing nothing, not doing harm.
   */
  useEffect(() => {
    const noteStarted = () => {
      const active = document.activeElement;
      if (active && bookerRef.current?.contains(active)) {
        startedRef.current = true;
      }
    };

    const refresh = () => {
      // Not over a booking in progress, and not while the tab is in the
      // background — a booker rebuilt out of sight is work nobody sees, and no
      // fresher by the time they look at it than the one asked for below, on
      // the way back in.
      if (startedRef.current || document.visibilityState !== "visible") return;
      setBookerKey((key) => key + 1);
    };

    // Coming back to the tab is both when the times are most likely to have
    // moved on and when somebody is about to act on them.
    const handleVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };

    window.addEventListener("blur", noteStarted);
    document.addEventListener("visibilitychange", handleVisibility);
    const timer = window.setInterval(refresh, AVAILABILITY_REFRESH_MS);

    return () => {
      window.removeEventListener("blur", noteStarted);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.clearInterval(timer);
    };
  }, []);

  return (
    // `h-full` so the booker takes the shell's whole content area, with a floor
    // under it: below that the month grid and the times beside it stop fitting
    // together, and the page is left to scroll instead of squeezing them.
    <div className="flex h-full min-h-[720px] flex-col p-page pt-page-top">
      {/* The trail back to where this was opened from, as every screen sitting
          under another one carries it. Settings first and then the Support tab:
          that tab is the only way in, and it is not a place of its own — see
          `settingsCrumbs`, which every screen under Settings starts from. */}
      <PageHeader
        title={heading}
        breadcrumbs={[...settingsCrumbs("support"), { label: heading }]}
      />

      {/* What they are moving, said on our own page. Cal marks the former time
          somewhere inside the booker, on a panel that is off the bottom of a
          phone — and somebody choosing a new time needs the old one in front of
          them while they choose, not one scroll away. */}
      {currentWhen && (
        <p className="mt-2 text-[13px] 3xl:text-[14px] text-ink-muted">
          Currently booked for{" "}
          <span className="font-medium text-ink">{currentWhen}</span>. Pick a new
          time below.
        </p>
      )}

      {/* `min-h-0` so this pane can be shorter than the embed inside it — a
          flex child defaults to its content's height, which would push the
          calendar past the bottom of the shell rather than scrolling it. */}
      <div ref={bookerRef} className="mt-4 3xl:mt-6 min-h-0 flex-1">
        <Cal
          key={bookerKey}
          namespace={CAL_NAMESPACE}
          calLink={CAL_LINK}
          style={{ width: "100%", height: "100%", overflow: "scroll" }}
          config={{
            layout: "month_view",
            theme: "light",
            // On a phone the month grid and the times cannot share the width,
            // so the slots take the screen once a day is picked.
            useSlotsViewOnSmallScreen: "true",
            name: fullName,
            email,
            notes,
            // What turns the booker from an empty form into the existing call:
            // Cal reads it as a query param, loads that booking, and confirms
            // as a move rather than a second slot. Spread rather than set to
            // `undefined`, which Cal would put in the URL as the string.
            ...(rescheduleUid ? { rescheduleUid } : {}),
          }}
        />
      </div>

      {/* The way onward, under the booker rather than up beside the heading.
          Cal's "This meeting is scheduled" is the end of the booker but not of
          the errand — and it is Cal's own account of the booking, gone the
          moment this page is left. The Support tab is where ours lives: the
          call, when it is, the room to join it by and the way to move it again.
          So the button says what is at the other end of it rather than which
          screen that is, and the trail under the heading names the screen.

          It sits at the foot of the screen because that is where somebody is
          when they have finished reading the confirmation: the details, the
          calendar buttons and Cal's own footer all scroll past inside the
          frame, and a button at the top is behind them by then. Outside the
          frame, so it stays put rather than scrolling away with them.

          The bar the forms wear — `PinnedActionBar`: run out to the shell's
          edges and stuck to the foot of the screen so it is there whether or
          not the shell has scrolled. White over the page's off-white and ruled
          off along the top on a desktop; on a phone the shared bar drops both
          and blurs what passes under instead. See `AddEnquiryForm`, which is
          where this treatment comes from — a screen that ends in one action
          should end in it the same way everywhere.

          The negative margins take the screen's own padding with them: sideways
          so the bar meets the shell's edges, and downwards so it meets the foot
          of it. Left in, that bottom padding is a strip of page below a bar
          that is meant to be the end of it.

          Kept shallow — a small button in a thin bar — because every pixel of
          it comes off the booker above: the pane is what is left of the screen
          once this has taken its share, and a deep footer costs the month grid
          the row it needs to fit. */}
      {scheduled && (
        <PinnedActionBar className="-mx-page -mb-page mt-3 border-t border-line bg-white px-page py-2.5 3xl:py-3">
          <div className="flex flex-wrap items-center justify-end gap-3">
            {/* A link, not a button: it goes somewhere. See `buttonClasses`. */}
            <Link
              to={SUPPORT_SETTINGS_PATH}
              className={`${buttonClasses({ variant: "primary", size: "sm" })} min-w-[140px]`}
            >
              View booking details
            </Link>
          </div>
        </PinnedActionBar>
      )}
    </div>
  );
};

export default BookCallPage;
